import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ChevronRight, Loader2 } from "lucide-react";

export default function SponsorView() {
  const [requests, setRequests] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([api.listRequests(), api.summary()])
      .then(([r, s]) => { setRequests(r); setSummary(s); })
      .catch((e) => setErr(e.message));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="sponsor-page">
      <header className="mb-12">
        <p className="eyebrow">Sponsor view</p>
        <h1 className="font-display text-4xl md:text-5xl text-[var(--ink-heading)] mt-3 leading-tight">
          Federated operating view
        </h1>
        <p className="text-[var(--ink-secondary)] mt-4 max-w-3xl text-lg leading-relaxed">
          Each row represents a synthetic data reuse request. Open one to see
          custody, authority, evidence boundary, AI advisory note, review
          timeline and the KATLAS receipt status — without any raw data leaving
          its source system.
        </p>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10" data-testid="sponsor-summary">
          {[
            ["awaiting_custody", "Awaiting custody"],
            ["awaiting_policy", "Awaiting policy"],
            ["awaiting_assurance", "Awaiting assurance"],
            ["approved", "Approved"],
            ["escalated", "Escalated"],
          ].map(([k, label]) => (
            <div key={k} className="border border-slate-200 bg-white p-4">
              <p className="text-xs text-[var(--ink-muted)] uppercase tracking-[0.14em]">{label}</p>
              <p className="font-display text-3xl text-[var(--ink-heading)] mt-2" data-testid={`summary-${k}`}>
                {summary.by_status?.[k] || 0}
              </p>
            </div>
          ))}
        </div>
      )}

      {err && <p className="text-sm text-red-700" data-testid="sponsor-error">{err}</p>}
      {!requests && !err && (
        <div className="flex items-center gap-2 text-[var(--ink-muted)]" data-testid="sponsor-loading">
          <Loader2 className="animate-spin" size={16} /> Loading requests…
        </div>
      )}

      {requests && (
        <ul className="border-t border-slate-200" data-testid="sponsor-request-list">
          {requests.map((r) => (
            <li key={r.id} className="border-b border-slate-200 py-6 hover:bg-[var(--bg-secondary)]/40 transition-colors">
              <Link to={`/request/${r.id}`} className="grid grid-cols-12 gap-4 items-center" data-testid={`request-row-${r.id}`}>
                <div className="col-span-12 md:col-span-7">
                  <p className="font-mono-ui text-xs text-[var(--ink-mono)] tracking-[0.14em]">{r.id}</p>
                  <p className="font-display text-xl text-[var(--ink-heading)] mt-1">{r.title}</p>
                  <p className="text-sm text-[var(--ink-secondary)] mt-1.5 max-w-2xl line-clamp-2">
                    {r.purpose_statement}
                  </p>
                </div>
                <div className="col-span-6 md:col-span-3 text-sm text-[var(--ink-secondary)]">
                  <p className="text-xs text-[var(--ink-muted)] uppercase tracking-[0.14em] mb-1">Source</p>
                  <p>{r.source_system}</p>
                </div>
                <div className="col-span-6 md:col-span-2 flex md:justify-end items-center gap-3">
                  <StatusBadge status={r.status} />
                  <ChevronRight size={16} className="text-[var(--ink-muted)]" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
